import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { GreenButton, WhiteButton } from '@marketing/components/buttons';
import { NavigationItemProps } from '../../types';

import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import styles from './style.module.scss';

interface Props {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  isSticky: boolean;
  navigationItems: NavigationItemProps[];
}

const NavBar: React.FC<Props> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, []);

  return (
    <nav className={cx({ 'expanded': isExpanded })} style={{ position: 'relative' }}>
      <div
        className={cx(
          styles.navbar,
          'align-items-center d-flex flex-1 gap-s',
          { [styles.sticky]: props.isSticky }
        )}
      >
        <div>
          <a href="/">
            <img
              alt="Magoosh logo"
              className={cx(styles.logo, { [styles.sticky]: props.isSticky })}
              src={MagooshLogo}
            />
          </a>
        </div>

        <div className="d-flex flex-1 hidden-xs hidden-sm justify-content-center">
          <ul className={styles.navbarItems}>
            {
              props.navigationItems.map((navItem, idx) => (
                <li key={idx}>
                  <a
                    className="text-inter text-medium"
                    dangerouslySetInnerHTML={{ __html: navItem.content }}
                    href={navItem.href}
                  />
                </li>
              ))
            }
          </ul>
        </div>

        <div className="hidden-xs hidden-sm">
          <div className="d-flex gap-lg-s">
            {
              props.isLoggedIn ? (
                <GreenButton href="/" size="sm">
                  Dashboard
                </GreenButton>
              ) : (
                <>
                  <WhiteButton href="/login" size="sm">
                    Log in
                  </WhiteButton>

                  {
                    !props.hideSignUp && (
                      <GreenButton href="/plans" size="sm">
                      Sign up
                    </GreenButton>
                    )
                  }
                </>
              )
            }
          </div>
        </div>

        <div className="flex-1 visible-xs visible-sm">
          <div className="d-flex flex-1 flex-lg-none justify-content-end">
            <button className={styles.navbarToggle} onClick={() => setIsExpanded(!isExpanded)}>
              <span className="sr-only">Toggle navigation</span>
              <span className={styles.iconBar} />
              <span className={styles.iconBar} />
              <span className={styles.iconBar} />
            </button>
          </div>
        </div>
      </div>

      <div
        className="visible-xs visible-sm"
        style={{
          background: '#fff',
          boxShadow: isExpanded ? '0 0 0 1px rgba(0, 0, 0, 0.1), 0 6px 6px -3px rgba(0, 0, 0, 0.07), 0 2px 2px -1px rgba(0, 0, 0, 0.07)' : 'none',
          height: isExpanded ? contentHeight : 0,
          marginLeft: -20,
          overflow: 'hidden',
          padding: '0 20px',
          position: 'absolute',
          top: props.isSticky ? 58 : 74,
          transition: 'height 200ms',
          width: 'calc(100% + 40px)'
        }}
      >
        <div className="d-flex flex-column gap-m u-padding-V-m" ref={ref}>
          <div>
            <ul className={styles.navbarItems}>
              {
                props.navigationItems.map((navItem, idx) => (
                  <li key={idx}>
                    <a href={navItem.href} dangerouslySetInnerHTML={{ __html: navItem.content }} />
                  </li>
                ))
              }
            </ul>
          </div>

          <div className="d-flex gap-s">
            {
              props.isLoggedIn ? (
                <div className="flex-1">
                  <GreenButton block href="/" size="sm">
                    Dashboard
                  </GreenButton>
                </div>
              ) : (
                <>
                  <div className="flex-1">
                    <WhiteButton block href="/login" size="sm">
                      Log in
                    </WhiteButton>
                  </div>

                  {
                    !props.hideSignUp && (
                      <div className="flex-1">
                        <GreenButton block href="/plans" size="sm">
                          Sign up
                        </GreenButton>
                      </div>
                    )
                  }
                </>
              )
            }
          </div>
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
